import React, { useState } from "react";
import { FaEdit, FaTrash, FaPlusCircle, FaBoxOpen } from "react-icons/fa";
import ItemModal from "./ItemModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { toast } from "react-toastify";

const BillItemsTable = ({
  items = [],
  onUpdateItems,
  onLineDiscountChange,
  deletedItems,
  setDeletedItems,
  isPublished,
  pharmacy_id,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  const openModal = (item, index) => {
    if (isPublished) {
      toast.warn("Sorry! You cannot able to make any changes published bill");
      return; // Prevent editing if invoice is published
    }
    setCurrentItem(item || null); // Set the selected item for editing
    setCurrentItemIndex(index); // Track the index of the item being edited
    setIsModalOpen(true);
  };

  const handleSaveItem = (updatedItem) => {
    const updatedItems = [...items];

    if (currentItemIndex !== null) {
      // Update an existing item
      updatedItems[currentItemIndex] = updatedItem;
    } else {
      // Add a new item
      updatedItems.push(updatedItem);
    }

    onUpdateItems(updatedItems);
    onLineDiscountChange(
      updatedItems.some((item) => item.lineDiscountValue > 0)
    );
    setIsModalOpen(false);
  };

  const confirmDeleteItem = (index) => {
    if (isPublished) {
      toast.warn("Sorry! You cannot able to make any changes published bill");
      return; // Prevent editing if invoice is published
    }
    setCurrentItemIndex(index);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteItem = () => {
    if (currentItemIndex !== null) {
      // Capture the deleted item and set its quantity to 0
      const itemToDelete = {
        ...items[currentItemIndex],
        quantity: 0,
      };

      // Add the deleted item to the deletedItems array
      setDeletedItems((prevDeleted) => [...prevDeleted, itemToDelete]);

      // Remove the item from the main items array
      const updatedItems = items.filter((_, i) => i !== currentItemIndex);
      onUpdateItems(updatedItems);
      onLineDiscountChange(
        updatedItems.some((item) => item.lineDiscountValue > 0)
      );

      console.log("Deleted Items:", [...deletedItems, itemToDelete]);
    }
    setIsDeleteModalOpen(false);
  };

  const calculateTaxes = (item) => {
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item?.sellingPrice) || 0;
    const taxableValue = quantity * unitPrice;

    const cgst = (taxableValue * (item.cgstRate || 0)) / 100;
    const sgst = (taxableValue * (item.sgstRate || 0)) / 100;
    const igst = (taxableValue * (item.igstRate || 0)) / 100;

    const discount =
      item.lineDiscountType === "percentage"
        ? (taxableValue * (item.lineDiscountValue || 0)) / 100
        : item.lineDiscountValue || 0;

    const netTaxableValue = taxableValue - discount;

    return {
      taxableValue,
      discount,
      netTaxableValue,
      cgst,
      sgst,
      igst,
      totalTax: cgst + sgst + igst,
      totalAmount: netTaxableValue + cgst + sgst + igst,
    };
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
      <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
        <FaPlusCircle className="mr-2 text-green-600" />
        Bill Items
      </h3>

      <div className="overflow-x-auto">
        {items.length === 0 ? (
          // Display an empty state when there are no items
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <FaBoxOpen className="text-5xl text-gray-400 mb-4" />
            <p className="text-lg font-semibold">No items added yet</p>
            <p className="text-sm">
              Click "Add Item" to start adding bill items.
            </p>
          </div>
        ) : (
          <table className="table-auto w-full text-left border-collapse">
            <thead className="bg-gray-200">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2">Quantity</th>
                <th className="px-4 py-2">Unit Price</th>
                <th className="px-4 py-2">Selling Price</th>
                <th className="px-4 py-2">Discount</th>
                <th className="px-4 py-2">Net Taxable Value</th>
                <th className="px-4 py-2">CGST</th>
                <th className="px-4 py-2">SGST</th>
                <th className="px-4 py-2">IGST</th>
                <th className="px-4 py-2">Total Tax</th>
                <th className="px-4 py-2">Total Amount</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const {
                  taxableValue,
                  discount,
                  netTaxableValue,
                  cgst,
                  sgst,
                  igst,
                  totalTax,
                  totalAmount,
                } = calculateTaxes(item);

                return (
                  <tr
                    key={index}
                    className="border-t hover:bg-gray-100 transition"
                  >
                    <td className="px-4 py-2">{item.itemName}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {item.description || "-"}
                    </td>
                    <td className="px-4 py-2">{item.quantity}</td>
                    <td className="px-4 py-2">₹{item.unitPrice.toFixed(2)}</td>
                    <td className="px-4 py-2">
                      ₹{item?.sellingPrice?.toFixed(2)}
                    </td>
                    <td className="px-4 py-2">
                      {item.lineDiscountType === "percentage"
                        ? `${item.lineDiscountValue}%`
                        : `₹${item.lineDiscountValue}`}
                    </td>
                    <td className="px-4 py-2">₹{netTaxableValue.toFixed(2)}</td>
                    <td className="px-4 py-2">₹{cgst.toFixed(2)}</td>
                    <td className="px-4 py-2">₹{sgst.toFixed(2)}</td>
                    <td className="px-4 py-2">₹{igst.toFixed(2)}</td>
                    <td className="px-4 py-2">₹{totalTax.toFixed(2)}</td>
                    <td className="px-4 py-2">₹{totalAmount.toFixed(2)}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => openModal(item, index)}
                        className="text-blue-600 hover:text-blue-800 mr-2"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => confirmDeleteItem(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <button
        onClick={() => openModal(null, null)}
        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 flex items-center"
      >
        <FaPlusCircle className="mr-2" /> Add Item
      </button>

      {isModalOpen && (
        <ItemModal
          item={currentItem}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveItem}
          pharmacy_id={pharmacy_id}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteItem}
        />
      )}
    </div>
  );
};

export default BillItemsTable;
